import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useOnboardingAuth } from '../../hooks/useOnboardingAuth';
import { vendorProfileService, type VendorLocale } from '../../services/vendorProfileService';

// S3 asset URL
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

const OnboardingStep2: React.FC = () => {
  usePageTitle('Company Profile');
  
  const navigate = useNavigate();
  const { vendor, isLoading } = useOnboardingAuth({ currentStep: 1 });
  
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [currentLang, setCurrentLang] = useState<'en' | 'ar'>('en');
  const [locale, setLocale] = useState<VendorLocale>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with vendor data (and locale when present)
  useEffect(() => {
    if (vendor) {
      const initialLocale: VendorLocale =
        vendor.locale ??
        (vendor.companyName || vendor.companyDescription
          ? {
              en: {
                ...(vendor.companyName ? { companyName: vendor.companyName } : {}),
                ...(vendor.companyDescription ? { companyDescription: vendor.companyDescription } : {}),
              },
            }
          : {});

      setLocale(initialLocale);
      setCurrentLang('en');

      const enEntry = initialLocale.en ?? {};
      setCompanyName(enEntry.companyName ?? vendor.companyName ?? '');
      setCompanyDescription(enEntry.companyDescription ?? vendor.companyDescription ?? '');
      setCompanyWebsite(vendor.companyWebsite ?? '');
    }
  }, [vendor]);

  const handleLanguageChange = useCallback(
    (newLang: 'en' | 'ar') => {
      const trimmedName = companyName.trim();
      const trimmedDescription = companyDescription.trim();
      const nextLocale: VendorLocale = {
        ...locale,
        [currentLang]: {
          ...(locale[currentLang] || {}),
          ...(trimmedName ? { companyName: trimmedName } : {}),
          ...(trimmedDescription ? { companyDescription: trimmedDescription } : {}),
        },
      };
      setLocale(nextLocale);
      setCurrentLang(newLang);
      const targetEntry = nextLocale[newLang] || {};
      setCompanyName(targetEntry.companyName ?? '');
      setCompanyDescription(targetEntry.companyDescription ?? '');
    },
    [companyName, companyDescription, currentLang, locale]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);

    if (!vendor) {
      toast.error('No vendor session found. Please try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Snapshot current language into locale, then build payload with en as primary
      const finalLocale: VendorLocale = {
        ...locale,
        [currentLang]: {
          ...(locale[currentLang] || {}),
          ...(companyName.trim() ? { companyName: companyName.trim() } : {}),
          ...(companyDescription.trim() ? { companyDescription: companyDescription.trim() } : {}),
        },
      };
      // Backend requires companyName/companyDescription; use en as primary, fallback to ar
      const primaryName = finalLocale.en?.companyName ?? finalLocale.ar?.companyName ?? companyName.trim();
      const primaryDescription = finalLocale.en?.companyDescription ?? finalLocale.ar?.companyDescription ?? companyDescription.trim();

      const response = await vendorProfileService.updateCompanyProfile({
        vendorId: vendor.vendorId,
        companyName: primaryName,
        companyDescription: primaryDescription,
        companyWebsite: companyWebsite.trim(),
        locale: (finalLocale.en || finalLocale.ar) ? finalLocale : undefined,
        nextStep: 2, // Move to step 3 (Profile Image)
      });

      if (response.success) {
        toast.success('Company profile updated! Proceeding to next step...');
        // Navigate to next step after a brief delay
        setTimeout(() => {
          navigate('/onboarding/step3');
        }, 1000);
      } else {
        // Don't show error here - let the catch block handle it
        setIsSubmitting(false);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      
      // Handle specific error messages from backend
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to update profile. Please try again.';
      
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };


  const progress = 40; // 2 of 5 steps = 40%

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 0',
          width: '100vw'
        }}
      >
        <CircularProgress size={60} sx={{ color: '#67314f', marginBottom: '20px' }} />
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            color: '#181818',
            textAlign: 'center'
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  // Show form only if vendor is authenticated
  if (!vendor) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 0',
        width: '100vw'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          width: '120px',
          height: '94px',
          marginBottom: '40px'
        }}
      >
        <Box
          component="img"
          src={logoImg}
          alt="Eventi Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          width: '100%',
          maxWidth: '540px',
          padding: '0 20px'
        }}
      >
          {/* Title Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '44px',
                lineHeight: 1.15,
                letterSpacing: '-0.44px',
                color: '#181818',
                marginBottom: '16px',
                textAlign: 'left'
              }}
            >
              Complete profile
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.4,
                color: '#181818',
                marginBottom: '16px',
                textAlign: 'left'
              }}
            >
              Complete your company Eventi profile to access your management dashboard. This will only take couple of minutes.
            </Typography>
            
            {/* Progress Indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#181818'
                }}
              >
                2 of 5
              </Typography>
            </Box>
            
            {/* Progress Bar */}
            <Box sx={{ width: '100%', margin: '0 auto' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: '#f0f0f0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#67314f',
                    borderRadius: '2px'
                  }
                }}
              />
            </Box>
          </Box>

          {/* Form Section */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {/* Section Title */}
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: 1.2,
                color: '#181818',
                marginBottom: '8px'
              }}
            >
              Company profile
            </Typography>

            {/* Language toggle for company name/description */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#181818',
                }}
              >
                Language
              </Typography>
              <ToggleButtonGroup
                value={currentLang}
                exclusive
                onChange={(_, v) => v != null && handleLanguageChange(v)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '14px',
                    textTransform: 'none',
                    border: '1px solid #e0e0e0',
                    '&.Mui-selected': { backgroundColor: '#67314f', color: 'white', '&:hover': { backgroundColor: '#5a2a42' } },
                  },
                }}
              >
                <ToggleButton value="en">EN</ToggleButton>
                <ToggleButton value="ar">AR</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Company Name Field */}
            <TextField
              fullWidth
              placeholder="Company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f6f8f8',
                  borderRadius: '12px',
                  height: '56px',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '16px',
                  color: companyName ? '#181818' : '#93989c',
                  '&::placeholder': {
                    color: '#93989c',
                    opacity: 1
                  }
                }
              }}
            />

            {/* Company Description Field */}
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Company description"
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f6f8f8',
                  borderRadius: '12px',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '16px',
                  color: companyDescription ? '#181818' : '#93989c',
                  '&::placeholder': {
                    color: '#93989c',
                    opacity: 1
                  }
                }
              }}
            />

            {/* Company Website Field */}
            <TextField
              fullWidth
              placeholder="Company website"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f6f8f8',
                  borderRadius: '12px',
                  height: '56px',
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '16px',
                  color: companyWebsite ? '#181818' : '#93989c',
                  '&::placeholder': {
                    color: '#93989c',
                    opacity: 1
                  }
                }
              }}
            />

            {/* Continue Button */}
            <Button
              type="submit"
              fullWidth
              disabled={isSubmitting}
              sx={{
                height: '56px',
                backgroundColor: '#67314f',
                borderRadius: '16px',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                letterSpacing: '-0.32px',
                textTransform: 'none',
                color: 'white',
                marginTop: '16px',
                '&:hover': {
                  backgroundColor: '#5a2a42'
                },
                '&:disabled': {
                  backgroundColor: '#93989c',
                  color: 'white'
                }
              }}
            >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Continue'
            )}
            </Button>

          </Box>
        </Box>
      </Box>
  );
};

export default OnboardingStep2;
